import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faCog, faEdit, faTrash, faSpinner,
    faBoxes, faDollarSign, faRuler, faAward, faGasPump
} from '@fortawesome/free-solid-svg-icons';
import './StockItemVariantCard.css';
import axiosInstance from '../utils/axiosConfig';
import StockItemVariantForm from './StockItemVariantForm';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import ContractDetailsModal from './ContractDetailsModal';
import StockpilePanel from './Stockpile/StockpilePanel';
import StockVariantMovementPanel from './StockVariantMovementPanel';
import { getErrorMessage } from '../utils/errorMessages';
import {
    MIXED_FUELS_FIELDS,
    isMixedFuelsSubtype,
    hasMixedFuelsSpec,
    getMixedFuelsLabel,
    getMixedFuelsShorthand,
} from '../data/mixedFuelsSpec';

const StockItemVariantCard = ({ stockItemId, onVariantUpdate, triggerAddVariant, parentSubtype }) => {
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);
    const [deletingVariant, setDeletingVariant] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [variantToDelete, setVariantToDelete] = useState(null);
    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
    const [blockingRecords, setBlockingRecords] = useState(null);

    // Debug: Log state changes
    useEffect(() => {
        if (isFormOpen) {
            console.log('Modal opened for stock item:', stockItemId);
        }
    }, [isFormOpen, stockItemId]);

    // Handle external trigger to add variant
    useEffect(() => {
        if (triggerAddVariant) {
            setIsFormOpen(true);
        }
    }, [triggerAddVariant]);

    useEffect(() => {
        loadVariants();
    }, [stockItemId]);

    const loadVariants = async () => {
        try {
            setLoading(true);
            console.log(`Loading variants for stock item: ${stockItemId}`);
            const response = await axiosInstance.get(`/api/stock/stock-variants/?stock_item_id=${stockItemId}`);
            console.log('Variants loaded successfully:', response.data);
            setVariants(response.data.results || response.data);
        } catch (err) {
            console.error('Error loading variants:', err);
            
            // Handle different error types
            setError(getErrorMessage(err, 'Stock item could not be updated. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleVariantSuccess = () => {
        // Reload variants after successful creation/update
        loadVariants();
        // Notify parent component if callback provided
        if (onVariantUpdate) {
            onVariantUpdate();
        }
        // Clear editing state
        setEditingVariant(null);
    };

    const handleEditVariant = (variant) => {
        setEditingVariant(variant);
        setIsFormOpen(true);
    };

    const handleDeleteVariant = (variant) => {
        setVariantToDelete(variant);
        setDeleteModalOpen(true);
    };

    const confirmDeleteVariant = async () => {
        if (!variantToDelete) return;

        const variantId = variantToDelete.id;
        setDeletingVariant(variantId);
        
        try {
            await axiosInstance.delete(`/api/stock/stock-variants/${variantId}/`);
            console.log('Variant deleted successfully:', variantId);
            
            // Close modal and reset state
            setDeleteModalOpen(false);
            setVariantToDelete(null);
            setBlockingRecords(null);
            
            // Reload variants after successful deletion
            loadVariants();
            
            // Notify parent component
            if (onVariantUpdate) {
                onVariantUpdate();
            }
        } catch (err) {
            console.error('Error deleting variant:', err);
            
            // Check if this is a structured blocking error (HTTP 400 with blocking_records)
            if (err.response?.status === 400 && err.response?.data?.blocking_records) {
                // Extract structured error information
                const errorData = err.response.data;
                console.log('Variant deletion blocked due to relationships:', errorData);
                
                // Store blocking records for modal display
                setBlockingRecords(errorData.blocking_records);
                
                // Throw error with detail message for modal to display
                throw new Error(getErrorMessage(err, 'Cannot delete this product variant as it is linked to existing records.'));
            }
            
            // Handle different error types - throw error to be handled by modal
            if (err.response?.status === 401) {
                throw new Error('You are not authorized to delete this variant. Please log in again.');
            } else if (err.response?.status === 404) {
                throw new Error('Variant not found. It may have already been deleted.');
            } else if (err.response?.status === 400) {
                throw new Error(getErrorMessage(err, 'Stock item could not be updated. Please try again.'));
            } else if (err.response?.status >= 500) {
                throw new Error('Server error. Please try again later.');
            } else {
                throw new Error('Failed to delete variant. Please try again.');
            }
        } finally {
            setDeletingVariant(null);
        }
    };

    const closeDeleteModal = () => {
        if (!deletingVariant) {
            setDeleteModalOpen(false);
            setVariantToDelete(null);
            setBlockingRecords(null);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatSourceType = (sourceType) => {
        const sourceTypeMap = {
            'manual': 'Manual',
            'contract': 'Contract',
            'delivery': 'Delivery Creation',
            'recap': 'Trade Recap'
        };
        return sourceTypeMap[sourceType] || sourceType;
    };

    const handleContractClick = (variant) => {
        if (variant.contract_analysis) {
            setSelectedAnalysisId(variant.contract_analysis);
            setContractModalOpen(true);
        }
    };

    const generateVariantDisplayName = (variant) => {
        const parts = [];
        
        // Show shortname if available, otherwise fallback to quality
        if (variant.shortname && typeof variant.shortname === 'string' && variant.shortname.trim()) {
            parts.push(variant.shortname.trim());
        } else if (variant.quality && typeof variant.quality === 'string' && variant.quality.trim()) {
            const quality = variant.quality.trim();
            parts.push(quality.length > 20 ? quality.substring(0, 20) + '...' : quality);
        } else {
            parts.push('N/A');
        }
        
        if (variant.quantity && !isNaN(variant.quantity) && Number(variant.quantity) > 0) {
            const unitName = variant.unit_name || 'Unit';
            const formatted = Number(variant.quantity).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            parts.push(`${formatted} ${unitName}`);
        }
        
        // Format: {shortname} | {quantity}
        return parts.join(' | ');
    };

    if (loading) {
        return (
            <div className="variant-loading">
                <div className="loading-spinner-small"></div>
                <span>Loading variants...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="variant-error">
                <p>{error}</p>
                <button onClick={loadVariants} className="btn-retry">
                    Try Again
                </button>
            </div>
        );
    }

    if (!variants.length) {
        return (
            <div className="no-variants">
                <div className="no-variants-content">
                    <FontAwesomeIcon icon={faBoxes} className="no-variants-icon" />
                    <h4>No Variants</h4>
                    <p>This product item doesn't have any variants yet.</p>
                    <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => setIsFormOpen(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Add First Variant
                    </button>
                </div>
                
                            <StockItemVariantForm
                isOpen={isFormOpen}
                onClose={() => {
                    setIsFormOpen(false);
                    setEditingVariant(null);
                }}
                onSuccess={handleVariantSuccess}
                stockItemId={stockItemId}
                editingVariant={editingVariant}
                parentSubtype={parentSubtype}
            />
            
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDeleteVariant}
                title="Delete Product Variant"
                message="Are you sure you want to delete this product variant? This action cannot be undone and will permanently remove all associated data."
                itemName={variantToDelete ? generateVariantDisplayName(variantToDelete) : ""}
                loading={!!deletingVariant}
                deleteButtonText="Delete Variant"
            />
        </div>
    );
    }

    return (
        <div className="stock-variants-container">

            
            <div className="variants-grid">
                {variants.map((variant) => (
                    <div key={variant.id} className="variant-card">
                        <div className="variant-header">
                            <div className="variant-info">
                                <span className="variant-id">
                                    {generateVariantDisplayName(variant)}
                                </span>
                                {variant.has_movements && variant.unit_name && (
                                    <span
                                        className="variant-on-hand"
                                        title="Live stockpile balance from the ledger"
                                    >
                                        On hand: {Number(variant.on_hand_balance || 0)
                                            .toLocaleString('en-US', { maximumFractionDigits: 2 })
                                            .replace(/,/g, ' ')} {variant.unit_name}
                                    </span>
                                )}
                                <span className="variant-source">
                                    {variant.source_type === 'contract' && variant.contract_analysis ? (
                                        <span 
                                            className="contract-link interactive-document-link"
                                            onClick={() => handleContractClick(variant)}
                                            style={{ 
                                                color: '#0066cc',
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                fontWeight: '500',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #dee2e6',
                                                transition: 'all 0.2s ease',
                                                display: 'inline-block'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#f8f9fa';
                                                e.target.style.borderColor = '#adb5bd';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#ffffff';
                                                e.target.style.borderColor = '#dee2e6';
                                            }}
                                        >
                                            Contract
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                        
                        <div className="variant-details">
                            {/* Fuels carry properties, not a size. `size` is a cargo
                                concept and is empty on every product in the database,
                                so on a fuels product it was a permanent "N/A" in the
                                most prominent tile. This shows the ISO 8217 shorthand
                                instead -- the format the desk actually quotes -- and
                                the standalone "Bunker Fuel Spec" row below is gone,
                                because showing the same string twice is not more
                                information. Cargo keeps Size unchanged (pinned). */}
                            {isMixedFuelsSubtype(parentSubtype) ? (
                                <div className="detail-row">
                                    <div className="detail-item">
                                        <FontAwesomeIcon icon={faGasPump} className="detail-icon" />
                                        <div className="detail-content">
                                            <span className="detail-label">Fuel Spec</span>
                                            {hasMixedFuelsSpec(variant.spec_attributes) ? (
                                                <span
                                                    className="detail-value quality"
                                                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                                                >
                                                    {getMixedFuelsShorthand(variant.spec_attributes)
                                                        || MIXED_FUELS_FIELDS
                                                            .filter((f) => variant.spec_attributes?.[f.key])
                                                            .map((f) => `${f.label}: ${getMixedFuelsLabel(f.key, variant.spec_attributes[f.key])}`)
                                                            .join(' | ')}
                                                </span>
                                            ) : (
                                                <span className="detail-value detail-value-muted">
                                                    Not specified yet
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="detail-row">
                                    <div className="detail-item">
                                        <FontAwesomeIcon icon={faRuler} className="detail-icon" />
                                        <div className="detail-content">
                                            <span className="detail-label">Size</span>
                                            <span className="detail-value">
                                                {variant.size || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* The variant's own price wins. When it has none, the
                                server supplies the most recently linked deal's
                                price and says which deal -- an unattributed number
                                on a product card is worse than a blank.
                                The server only ever sends deals THIS person may
                                open, so the name printed here can never be a deal
                                the Movement panel below refuses to show. When the
                                only priced deal is one they may not open, neither
                                the price nor its name comes down, and the card
                                says that rather than "Not priced yet" -- which
                                would be the same untraded-looking lie. */}
                            <div className="detail-row">
                                {[
                                    { label: 'Purchase Price', side: 'purchase' },
                                    { label: 'Sale Price', side: 'sale' },
                                ].map(({ label, side }) => {
                                    const eff = variant.effective_prices || {};
                                    const value = eff[`${side}_price`];
                                    const source = eff[`${side}_source`];
                                    const sourceLabel = eff[`${side}_source_label`];
                                    const hiddenDeals = eff.hidden_deal_count || 0;
                                    return (
                                        <div className="detail-item" key={side}>
                                            <FontAwesomeIcon icon={faDollarSign} className="detail-icon" />
                                            <div className="detail-content">
                                                <span className="detail-label">{label}</span>
                                                {value ? (
                                                    <>
                                                        <span className="detail-value">
                                                            {formatCurrency(value, variant.price_currency)}
                                                        </span>
                                                        {source !== 'own' && sourceLabel && (
                                                            <span className="detail-price-source">
                                                                from {sourceLabel}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : hiddenDeals > 0 ? (
                                                    <span className="detail-value detail-value-muted">
                                                        {hiddenDeals === 1
                                                            ? 'Priced on a deal you do not have access to'
                                                            : `Priced on ${hiddenDeals} deals you do not have access to`}
                                                    </span>
                                                ) : (
                                                    <span className="detail-value detail-value-muted">
                                                        Not priced yet
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {variant.quality && (
                                <div className="quality-section">
                                    <div className="detail-item full-width">
                                        <FontAwesomeIcon icon={faAward} className="detail-icon" />
                                        <div className="detail-content">
                                            <span className="detail-label">Quality</span>
                                            <span className="detail-value quality">
                                                {variant.quality}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <StockpilePanel variant={variant} />

                            {/* Every deal this variant was bought and sold in.
                                Collapsed, and it only asks the server when it
                                is opened -- this screen lists many variants. */}
                            <StockVariantMovementPanel variantId={variant.id} />
                        </div>

                        <div className="variant-footer">
                            <div className="footer-left">
                                <span className="variant-date">
                                    Created {new Date(variant.created_at).toLocaleDateString()}
                                </span>
                                {variant.source_reference_id && (
                                    <span className="variant-reference">
                                        Ref: {variant.source_reference_id}
                                    </span>
                                )}
                            </div>
                            <div className="footer-actions">
                                <button 
                                    className="action-btn edit" 
                                    title="Edit Variant"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditVariant(variant);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button 
                                    className="action-btn delete" 
                                    title="Delete Variant"
                                    disabled={deletingVariant === variant.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteVariant(variant);
                                    }}
                                >
                                    {deletingVariant === variant.id ? (
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    ) : (
                                        <FontAwesomeIcon icon={faTrash} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
                            <StockItemVariantForm
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingVariant(null);
                    }}
                    onSuccess={handleVariantSuccess}
                    stockItemId={stockItemId}
                    editingVariant={editingVariant}
                    parentSubtype={parentSubtype}
                />
                
                <DeleteConfirmationModal
                    isOpen={deleteModalOpen}
                    onClose={closeDeleteModal}
                    onConfirm={confirmDeleteVariant}
                    title="Delete Product Variant"
                    message="Are you sure you want to delete this product variant? This action cannot be undone and will permanently remove all associated data."
                    itemName={variantToDelete ? generateVariantDisplayName(variantToDelete) : ""}
                    loading={!!deletingVariant}
                    deleteButtonText="Delete Variant"
                    blockingRecords={blockingRecords}
                />

                <ContractDetailsModal
                    isOpen={contractModalOpen}
                    onClose={() => setContractModalOpen(false)}
                    analysisId={selectedAnalysisId}
                />
            </div>
        );
};

export default StockItemVariantCard; 